import { FileText } from 'lucide-react'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HighlightedText } from '@/components/search/HighlightedText'
import { Badge } from '@/components/ui/badge'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import type {
  ChunkHit,
  DocumentHit,
  EntityHit,
  SearchContentResponse,
} from '@/services/api/case-search'
import { entityLabelColor } from '@/utils/entity-colors'
import { FileTypeBadge } from './DocumentBadges'

/** What the search answered so far, and where a chosen hit leads. */
export interface DocumentSearchResults {
  data?: SearchContentResponse
  isFetching: boolean
  isError: boolean
}

export interface DocumentSearchActions {
  onOpenDocument: (hit: DocumentHit) => void
  onOpenEntity: (hit: EntityHit) => void
  onOpenChunk: (hit: ChunkHit) => void
}

export interface DocumentSearchComboboxViewProps {
  value: string
  onChange: (value: string) => void
  results: DocumentSearchResults
  actions: DocumentSearchActions
}

/** The search asks nothing below this length, and the results stay closed. */
const MIN_QUERY = 2

function ScoreBadge({ score, normalized = false }: { score: number; normalized?: boolean }) {
  const { t } = useTranslation()
  // For entities (normalized=true) the score is 0-1 with explicit tiers (1.0 / 0.7 / 0.4).
  // For chunks the score is unbounded Lucene BM25 — render with a neutral variant.
  let variant: 'default' | 'secondary' | 'outline' = 'secondary'
  if (normalized) {
    if (score >= 0.9) variant = 'default'
    else if (score >= 0.5) variant = 'secondary'
    else variant = 'outline'
  }
  return (
    <Badge
      variant={variant}
      title={t('documents.search.score')}
      className="font-medium tabular-nums"
    >
      {score.toFixed(2)}
    </Badge>
  )
}

function ResultsGroup({
  label,
  count,
  children,
}: {
  label: string
  count: number
  children: ReactNode
}) {
  if (count === 0) return null
  return (
    <CommandGroup className="p-0 not-first:mt-2 not-first:border-t not-first:border-border/40 not-first:pt-2">
      <div className="flex items-center justify-between px-2.5 pt-2 pb-1.5">
        <span className="text-3xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {label}
        </span>
        <span className="rounded-sm bg-muted/50 px-1.5 py-0.5 text-3xs font-medium tabular-nums text-muted-foreground">
          {count}
        </span>
      </div>
      <div className="space-y-0.5">{children}</div>
    </CommandGroup>
  )
}

interface RowProps<Hit> {
  hit: Hit
  query: string
  onOpen: () => void
}

function DocumentRow({ hit, query, onOpen }: RowProps<DocumentHit>) {
  const { t } = useTranslation()
  return (
    <CommandItem
      value={`doc-${hit.document_id}`}
      onSelect={onOpen}
      className="flex items-center gap-2.5 rounded-md px-2.5 py-2"
    >
      <FileText className="size-3.5 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1 truncate text-sm">
        <HighlightedText
          text={hit.name ?? hit.filename ?? t('documents.search.untitled')}
          query={query}
        />
      </span>
      {hit.file_type && <FileTypeBadge fileType={hit.file_type} />}
      <ScoreBadge score={hit.score} normalized />
    </CommandItem>
  )
}

function EntityRow({ hit, query, onOpen }: RowProps<EntityHit>) {
  return (
    <CommandItem
      value={`entity-${hit.entity_id}-${hit.document_id ?? 'none'}`}
      onSelect={onOpen}
      className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-2.5 gap-y-0.5 rounded-md px-2.5 py-2 md:grid-cols-[6.5rem_minmax(0,1fr)_auto]"
    >
      <span
        className={`inline-flex max-w-full items-center justify-self-start truncate rounded px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider ${entityLabelColor(hit.entity_label)}`}
        title={hit.entity_label}
      >
        {hit.entity_label}
      </span>
      <span className="min-w-0 truncate text-sm">
        <HighlightedText
          text={hit.match_value || hit.entity_name || hit.entity_label}
          query={query}
        />
      </span>
      <span className="justify-self-end">
        <ScoreBadge score={hit.score} normalized />
      </span>
      {hit.document_name && (
        <span className="col-span-2 col-start-2 flex min-w-0 items-center gap-1.5 text-2xs text-muted-foreground">
          <FileText className="size-3 shrink-0" />
          <span className="truncate">{hit.document_name}</span>
        </span>
      )}
    </CommandItem>
  )
}

function ChunkRow({ hit, query, onOpen }: RowProps<ChunkHit>) {
  const { t } = useTranslation()
  return (
    <CommandItem
      value={`chunk-${hit.chunk_id}-${hit.document_id ?? 'none'}`}
      onSelect={onOpen}
      className="flex items-start gap-2.5 rounded-md px-2.5 py-2"
    >
      <FileText className="mt-[3px] size-3.5 shrink-0 text-muted-foreground" />
      <div className="flex flex-1 min-w-0 flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">
            {hit.document_name ?? t('documents.search.untitled')}
          </span>
          <span className="ml-auto flex shrink-0 items-center gap-1.5 text-[10.5px] tabular-nums text-muted-foreground">
            {hit.section && (
              <span className="max-w-[160px] truncate max-md:hidden">{hit.section}</span>
            )}
            {hit.section && hit.page_number ? (
              <span className="opacity-40 max-md:hidden">·</span>
            ) : null}
            {hit.page_number && (
              <span>{t('documents.search.page', { page: hit.page_number })}</span>
            )}
            <ScoreBadge score={hit.score} />
          </span>
        </div>
        <div className="line-clamp-2 text-sm leading-relaxed text-muted-foreground">
          <HighlightedText text={hit.snippet} query={query} />
        </div>
      </div>
    </CommandItem>
  )
}

/**
 * The search field of the documents page and its results. The results open under the field and as
 * wide as it is, up to a width a row reads well at: a long field must not stretch them across the app,
 * and a long value must not push them past it.
 */
export function DocumentSearchComboboxView({
  value,
  onChange,
  results,
  actions,
}: DocumentSearchComboboxViewProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const { data, isFetching, isError } = results
  const query = value.trim()
  const documents = data?.documents ?? []
  const entities = data?.entities ?? []
  const chunks = data?.chunks ?? []
  const hasResults = documents.length + entities.length + chunks.length > 0

  /** A hit without a document has nothing to open. */
  const openHit = (documentId: string | null, openIt: () => void) => {
    if (!documentId) return
    openIt()
    setOpen(false)
  }

  return (
    <Popover open={open && query.length >= MIN_QUERY} onOpenChange={setOpen}>
      <Command
        shouldFilter={false}
        className="overflow-visible bg-transparent! p-0! rounded-md!"
        onKeyDown={e => {
          if (e.key === 'Escape') setOpen(false)
        }}
      >
        <PopoverAnchor asChild>
          {/* The whole field, so the results line up with its edges and not with the text inside it. */}
          <div>
            <CommandInput
              value={value}
              onValueChange={v => {
                onChange(v)
                setOpen(true)
              }}
              onFocus={() => setOpen(true)}
              onClick={() => setOpen(true)}
              placeholder={t('documents.searchPlaceholder')}
              // Phone: the input fills the whole box, so a tap anywhere in it focuses.
              className="max-md:self-stretch"
              outerClassName="p-0"
              wrapperClassName="h-9! bg-card border-border focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-primary/15 transition-colors"
            />
          </div>
        </PopoverAnchor>
        <PopoverContent
          align="start"
          className="z-[600] w-[min(var(--radix-popover-trigger-width),calc(100vw-2rem))] max-w-2xl overflow-hidden p-0 max-md:mx-0"
          onOpenAutoFocus={e => e.preventDefault()}
        >
          <CommandList className="max-h-[min(28rem,calc(100vh-10rem))] overscroll-contain p-1.5">
            {isFetching && !data && (
              <div className="py-8 text-center text-xs text-muted-foreground/70">
                {t('documents.search.searching')}
              </div>
            )}
            {isError && (
              <CommandEmpty className="py-8 text-xs text-muted-foreground">
                {t('documents.search.failed')}
              </CommandEmpty>
            )}
            {!isFetching && !isError && data && !hasResults && (
              <CommandEmpty className="py-8 text-xs text-muted-foreground">
                {t('documents.search.noMatches')}
              </CommandEmpty>
            )}

            <ResultsGroup label={t('documents.search.documents')} count={documents.length}>
              {documents.map(hit => (
                <DocumentRow
                  key={`doc-${hit.document_id}`}
                  hit={hit}
                  query={query}
                  onOpen={() => openHit(hit.document_id, () => actions.onOpenDocument(hit))}
                />
              ))}
            </ResultsGroup>

            <ResultsGroup label={t('documents.search.entities')} count={entities.length}>
              {entities.map(hit => (
                <EntityRow
                  key={`${hit.entity_id}-${hit.chunk_id ?? 'no-chunk'}-${hit.document_id ?? 'no-doc'}`}
                  hit={hit}
                  query={query}
                  onOpen={() => openHit(hit.document_id, () => actions.onOpenEntity(hit))}
                />
              ))}
            </ResultsGroup>

            <ResultsGroup label={t('documents.search.content')} count={chunks.length}>
              {chunks.map(hit => (
                <ChunkRow
                  key={hit.chunk_id}
                  hit={hit}
                  query={query}
                  onOpen={() => openHit(hit.document_id, () => actions.onOpenChunk(hit))}
                />
              ))}
            </ResultsGroup>
          </CommandList>
        </PopoverContent>
      </Command>
    </Popover>
  )
}
